/**
 * Platform-agnostic scheduling helpers shared by the reminder/recurrence UI and
 * the notification layer. Pure date/RRULE math — no DOM, no i18n, no platform
 * APIs — so it is fully unit-testable and reusable across web and Tauri.
 *
 * Conventions:
 *  - Dates persisted on the todo (`due_date`, `reminder_at`) are ISO strings.
 *  - Calendar/time editing happens in the user's **local** timezone; helpers
 *    convert local date+time to a stored ISO and back.
 *  - "All-day" is inferred as local midnight (no dedicated column exists).
 /** Waits for the app to finish booting (skeleton overlay disappears). */

const PAD_WIDTH = 2;
const DAYS_IN_WEEK = 7;
const WEEKS_IN_GRID = 6;
const MONDAY = 1;
const SATURDAY = 6;
const MS_PER_MINUTE = 60_000;
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;
const MINUTES_IN_WEEK = 10_080;
const DEFAULT_INTERVAL = 1;

function pad2(value: number): string {
  return String(value).padStart(PAD_WIDTH, "0");
}

function atLocalMidnight(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

// ─────────────────────────────────────────────────────────────────────────────
// Calendar grid
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a fixed 6×7 matrix of `Date`s covering `monthIndex` (0-based) plus the
 * leading/trailing days needed to fill whole weeks. The fixed 6-row height keeps
 * the calendar from reflowing as the user pages between months.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function getMonthMatrix(
  year: number,
  monthIndex: number,
  weekStartsOn: number = MONDAY,
): Date[][] {
  const firstOfMonth = new Date(year, monthIndex, 1);
  const leadingDays = (firstOfMonth.getDay() - weekStartsOn + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  const cursor = new Date(year, monthIndex, 1 - leadingDays);

  const weeks: Date[][] = [];
  for (let week = 0; week < WEEKS_IN_GRID; week += 1) {
    const days: Date[] = [];
    for (let day = 0; day < DAYS_IN_WEEK; day += 1) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(days);
  }
  return weeks;
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Local date ⇄ ISO
// ─────────────────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` for a `Date` in the local timezone. */
export function toLocalDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/**
 * Splits a stored ISO string into a local date key and a `HH:MM` time. A local
 * midnight with 0 milliseconds is treated as "all-day" and yields `time: null`.
 * An explicit midnight time (00:00) is tagged with 1 millisecond.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function splitLocalDateTime(iso: string | null | undefined): {
  dateKey: string | null;
  time: string | null;
} {
  if (!iso) {
    return { dateKey: null, time: null };
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { dateKey: null, time: null };
  }
  const isMidnightAllDay =
    date.getHours() === 0 && date.getMinutes() === 0 && date.getMilliseconds() === 0;
  return {
    dateKey: toLocalDateKey(date),
    time: isMidnightAllDay ? null : `${pad2(date.getHours())}:${pad2(date.getMinutes())}`,
  };
}

/** Combines a local `YYYY-MM-DD` and optional `HH:MM` into a stored ISO string. */
export function combineLocalDateTime(dateKey: string, time: string | null): string {
  const dateParts = dateKey.split("-");
  const year = Number(dateParts[0]);
  const month = Number(dateParts[1]);
  const day = Number(dateParts[2]);
  const timeParts = time ? time.split(":") : [];
  const hours = Number(timeParts[0] ?? 0);
  const minutes = Number(timeParts[1] ?? 0);
  // Explicitly tag midnight (00:00) with 1 millisecond to distinguish it from a pure midnight all-day task
  const ms = time !== null && hours === 0 && minutes === 0 ? 1 : 0;
  return new Date(year, month - 1, day, hours, minutes, 0, ms).toISOString();
}

// ─────────────────────────────────────────────────────────────────────────────
// Quick date presets (à la Google Calendar)
// ─────────────────────────────────────────────────────────────────────────────

export type DatePresetKey = "today" | "tomorrow" | "weekend" | "nextWeek";

/** Local-midnight `Date`s for the quick presets, relative to `now`. */
export function getDatePresets(now: Date = new Date()): Record<DatePresetKey, Date> {
  const today = atLocalMidnight(now);

  const tomorrow = atLocalMidnight(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Upcoming Saturday (today if it's already Saturday).
  const weekend = atLocalMidnight(now);
  weekend.setDate(weekend.getDate() + ((SATURDAY - now.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK));

  // Next Monday (always in the future, never today).
  const nextWeek = atLocalMidnight(now);
  const daysUntilMonday = (MONDAY - now.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK || DAYS_IN_WEEK;
  nextWeek.setDate(nextWeek.getDate() + daysUntilMonday);

  return { today, tomorrow, weekend, nextWeek };
}

// ─────────────────────────────────────────────────────────────────────────────
// Reminder offsets (notify N before the due date)
// ─────────────────────────────────────────────────────────────────────────────

export const REMINDER_OFFSET_MINUTES = {
  AT_TIME: 0,
  MIN_10: 10,
  MIN_30: 30,
  HOUR_1: MINUTES_IN_HOUR,
  DAY_1: MINUTES_IN_DAY,
  WEEK_1: MINUTES_IN_WEEK,
} as const;

export type ReminderOffsetMinutes =
  (typeof REMINDER_OFFSET_MINUTES)[keyof typeof REMINDER_OFFSET_MINUTES];

/** Absolute reminder time = due date minus `offsetMinutes`. */
export function computeReminderAt(dueDateISO: string, offsetMinutes: number): string {
  return new Date(new Date(dueDateISO).getTime() - offsetMinutes * MS_PER_MINUTE).toISOString();
}

/**
 * The whole-minute gap between the due date and the reminder, or `null` when
 * either is missing/invalid. Used to pre-select the matching offset preset.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function deriveReminderOffsetMinutes(
  dueDateISO: string | null | undefined,
  reminderAtISO: string | null | undefined,
): number | null {
  if (!dueDateISO || !reminderAtISO) {
    return null;
  }
  const dueMs = new Date(dueDateISO).getTime();
  const reminderMs = new Date(reminderAtISO).getTime();
  if (Number.isNaN(dueMs) || Number.isNaN(reminderMs)) {
    return null;
  }
  return Math.round((dueMs - reminderMs) / MS_PER_MINUTE);
}

/**
 * The effective time a reminder should fire: the explicit `reminder_at` when
 * set, otherwise the `due_date`. This is the single source of truth shared by
 * the notification query and the scheduling adapters.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function effectiveReminderTime(todo: {
  due_date?: string | null;
  reminder_at?: string | null;
}): string | null {
  return todo.reminder_at ?? todo.due_date ?? null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Recurrence (RRULE subset: FREQ, INTERVAL, BYDAY, UNTIL)
// ─────────────────────────────────────────────────────────────────────────────

export type RecurrenceFreq = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type Weekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export const WEEKDAYS_ORDER: readonly Weekday[] = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

/** Maps a JS `Date.getDay()` (0=Sun) to an RRULE weekday token. */
export const JS_DAY_TO_WEEKDAY: readonly Weekday[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

export interface ParsedRecurrence {
  freq: RecurrenceFreq;
  interval: number;
  byDays: Weekday[];
  until: string | null;
}

const VALID_FREQS: readonly RecurrenceFreq[] = ["DAILY", "WEEKLY", "MONTHLY", "YEARLY"];

function isRecurrenceFreq(value: string): value is RecurrenceFreq {
  return (VALID_FREQS as readonly string[]).includes(value);
}

function isWeekday(value: string): value is Weekday {
  return (WEEKDAYS_ORDER as readonly string[]).includes(value);
}

const RRULE_YEAR_END = 4;
const RRULE_MONTH_END = 6;
const RRULE_DAY_END = 8; // YYYYMMDD

/** RRULE `UNTIL` (`YYYYMMDD[THHMMSSZ]`) → ISO string (local midnight of that day, matching combineLocalDateTime). */
function parseUntil(raw: string | undefined): string | null {
  if (!raw || raw.length < RRULE_DAY_END) {
    return null;
  }
  const year = Number(raw.slice(0, RRULE_YEAR_END));
  const month = Number(raw.slice(RRULE_YEAR_END, RRULE_MONTH_END));
  const day = Number(raw.slice(RRULE_MONTH_END, RRULE_DAY_END));
  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return null;
  }
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
}

/** ISO string → RRULE `UNTIL` basic UTC form (`YYYYMMDDT235959Z`), using local fields to prevent timezone shifts. */
function formatUntil(iso: string): string {
  const date = new Date(iso);
  return `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}T235959Z`;
}

export function parseRecurrenceRule(rule: string | null | undefined): ParsedRecurrence | null {
  if (!rule) {
    return null;
  }
  const parts = new Map<string, string>();
  for (const segment of rule.split(";")) {
    const [key, value] = segment.split("=");
    if (key && value) {
      parts.set(key.toUpperCase(), value);
    }
  }

  const freq = parts.get("FREQ");
  if (!freq || !isRecurrenceFreq(freq)) {
    return null;
  }

  const interval = Number(parts.get("INTERVAL") ?? String(DEFAULT_INTERVAL));
  const byDays = (parts.get("BYDAY") ?? "")
    .split(",")
    .filter((token): token is Weekday => isWeekday(token));

  return {
    freq,
    interval:
      Number.isInteger(interval) && interval >= DEFAULT_INTERVAL ? interval : DEFAULT_INTERVAL,
    byDays,
    until: parseUntil(parts.get("UNTIL")),
  };
}

export function buildRecurrenceRule(recurrence: ParsedRecurrence): string {
  const segments = [`FREQ=${recurrence.freq}`];
  if (recurrence.interval > DEFAULT_INTERVAL) {
    segments.push(`INTERVAL=${recurrence.interval}`);
  }
  if (recurrence.freq === "WEEKLY" && recurrence.byDays.length > 0) {
    const ordered = WEEKDAYS_ORDER.filter((day) => recurrence.byDays.includes(day));
    segments.push(`BYDAY=${ordered.join(",")}`);
  }
  if (recurrence.until) {
    segments.push(`UNTIL=${formatUntil(recurrence.until)}`);
  }
  return segments.join(";");
}
